import { Dialog, DialogContent, Grid, Tab, Typography } from "@mui/material";
import { MprcContainerHCF, MprcHr, MprcRequestIndicator, MprcSpinnerIndicator, MprcTextMuted } from "components";
import { ButtonAdd } from "components/buttons";
import { MprcFilterEmptyDataset } from "components/filter/MprcFilterEmptyDataset";
import { IconButtonDelete } from "components/iconButtons";
import { IconButtonPreview } from "components/iconButtons/IconButtonPreview";
import { MprcChipTabs } from "components/MprcChipTabs";
import { MprcRequestSnackbarErrorReport } from "components/MprcRequestErrorReport";
import { GetRelationshipPublicationResponse, PublicationRelationshipOptions } from "generated/CoreApi_v1";
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { publishRelatedContentEvent } from "views/knowledge/components/card/CardEvents";
import { PublicationViewerDialog } from "views/knowledge/publication/dialogs/PublicationViewerDialog";
import { PublicationSelectorDrawer } from "views/knowledge/publication/drawers/PublicationSelectorDrawer";
import { TrainingRegistrationDialog } from "views/lms/student/Registration/TrainingRegistrationDialog";
import { TrainingRelationshipSelectorDrawer } from "views/lms/training/drawers/TrainingRelationshipSelectorDrawer";

////////////////////////////////////////////////////////////////////////////////

interface IPros {
    publicationId: string;
    title?: string;
    onClose: (changed: boolean) => void;
}

/////////////////////////////////////////////////////////////////////////////////

export const PublicationRelationshipDialog = (props: IPros) => {
    const { publicationId, title, onClose } = props;

    const api = useCoreApi();
    const { t } = useTranslation("knowledge");
    const { enqueueSnackbar } = useSnackbar();

    const [viewData, setViewData] = useState<GetRelationshipPublicationResponse[] | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [adding, setAdd] = useState<string | null>(null);
    const [deleting, setDelete] = useState<string | null>(null);
    const [dialog, setDialog] = useState<any>();
    const [option, setOption] = useState<PublicationRelationshipOptions>(PublicationRelationshipOptions.Publication);
    const changed = useRef(false);

    const closing = () => {
        onClose(changed.current);
        if (changed.current) publishRelatedContentEvent();
    };

    ///
    const handleAddButton = () => {

        if (viewData && viewData.length >= 15) {
            enqueueSnackbar(t("Feed.Card.Actions.PublicationRelationshipDialog.MaxLimitRelationshipMessage"), { variant: "error" });
            return;
        }

        setDialog(
            option == PublicationRelationshipOptions.Publication
                ? <PublicationSelectorDrawer
                    title={t("Feed.Card.Actions.PublicationRelationshipDialog.PublicationDrawerTitle")}
                    multi={false}
                    onClose={(pub) => {
                        setDialog(null);
                        if (pub?.length == 1) {
                            changed.current = true;
                            setAdd(pub[0].publicationId);
                        }
                    }}
                />
                : <TrainingRelationshipSelectorDrawer
                    title={t("Feed.Card.Actions.PublicationRelationshipDialog.TrainingDrawerTitle")}
                    multi={false}
                    onClose={(training) => {
                        setDialog(null);
                        if (training?.length == 1) {
                            changed.current = true;
                            setAdd(training[0].trainingId);
                        }
                    }}
                />
        );
    };

    ///
    const handlePreview = (item: GetRelationshipPublicationResponse) => {
        setDialog(
            option == PublicationRelationshipOptions.Publication
                ? <PublicationViewerDialog
                    publicationId={item.id}
                    onClose={() => setDialog(null)}
                />
                : <TrainingRegistrationDialog
                    trainingId={item.id}
                    readonly
                    onClose={() => setDialog(null)}
                />

        );
    };

    ////////////////////////////////////////////////////////////////////////////
    /// EFFECTS
    ////////////////////////////////////////////////////////////////////////////

    // Ao carregar dados...
    useEffect(() => {
        async function getData() {
            setViewData(null);

            try {
                var result = await api.v1.knowledgePublication.getRelationships({ publicationId, option });
                setViewData(result.data);
            }
            catch (e) {
                MprcRequestSnackbarErrorReport(e);
            }
            finally {
                setLoading(false);
            }
        }

        if (loading) {
            getData();
        }
    }, [loading]);


    // Ao adicionar relacionamentos
    useEffect(() => {
        async function postData() {
            try {
                var result = option == PublicationRelationshipOptions.Publication
                    ? await api.v1.knowledgePublication.addRelationshipPublication({ publicationId, relatedPublicationId: adding! })
                    : await api.v1.knowledgePublication.addRelationshipTraining({ publicationId, relatedTrainingId: adding! });

                if (result) {
                    changed.current = true;
                    setLoading(true);
                }
            }
            catch (e) {
                MprcRequestSnackbarErrorReport(e);
            }
            finally {
                setAdd(null);
            }
        }

        if (adding) {
            postData();
        }
    }, [adding]);

    // Ao remover relacionamentos
    useEffect(() => {
        async function postData() {
            try {
                var result = option == PublicationRelationshipOptions.Publication
                    ? await api.v1.knowledgePublication.removeRelationshipPublication({ publicationId, relatedPublicationId: deleting! })
                    : await api.v1.knowledgePublication.removeRelationshipTraining({ publicationId, relatedTrainingId: deleting! });

                if (result) {
                    changed.current = true;
                    setLoading(true);
                }
            }
            catch (e) {
                MprcRequestSnackbarErrorReport(e);
            }
            finally {
                setDelete(null);
            }
        }

        if (deleting) {
            postData();
        }
    }, [deleting]);

    ///
    useEffect(() => {
        setLoading(true);
    }, [option]);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    ///
    const RenderGrid = () => {
        return (
            <Grid container justifyContent="center" alignItems="center" spacing={1}>
                {
                    viewData?.map((item, index) => {
                        return (
                            <Grid key={item.id} width="100%">
                                <Grid container justifyContent="center" alignItems="center" spacing={1} wrap="nowrap">
                                    <Grid>
                                        <IconButtonPreview size="small" color="muted" onClick={() => handlePreview(item)} />
                                    </Grid>
                                    <Grid size="grow" overflow="hidden">
                                        <MprcTextMuted>
                                            <Typography
                                                variant="h6"
                                                noWrap
                                            >
                                                {item.title}
                                            </Typography>
                                        </MprcTextMuted>
                                    </Grid>
                                    <Grid>
                                        <IconButtonDelete
                                            size="small"
                                            color="muted"
                                            onClick={() => setDelete(item.id)}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        );
                    })
                }
            </Grid>
        );
    };

    return (
        <>

            {dialog}

            <Dialog
                open
                fullWidth
                maxWidth="sm"
                onClose={closing}
            >
                <DialogContent style={{ height: 400 }}>
                    <MprcContainerHCF
                        title={t("Feed.Card.Actions.PublicationRelationshipDialog.Title")}
                        subtitle={title}
                        onClose={closing}
                        header={
                            <>
                                <Grid container justifyContent="space-between" alignItems="center">
                                    <Grid>
                                        <ButtonAdd
                                            size="large"
                                            text={t("Feed.Card.Actions.PublicationRelationshipDialog.AddButtonText")}
                                            color="muted"
                                            onClick={handleAddButton}
                                        />
                                    </Grid>
                                    <Grid>
                                        <MprcChipTabs size="small" value={option} onChange={(e, value) => setOption(value)}>
                                            <Tab value={PublicationRelationshipOptions.Publication} label={t("Feed.Card.Actions.PublicationRelationshipDialog.Options.Publications")} />
                                            <Tab value={PublicationRelationshipOptions.Training} label={t("Feed.Card.Actions.PublicationRelationshipDialog.Options.Trainings")} />
                                        </MprcChipTabs>
                                    </Grid>
                                </Grid>
                                <MprcHr />
                            </>
                        }
                        content={
                            <>
                                <MprcRequestIndicator type="post" visible={adding != null || deleting != null} />

                                {
                                    loading && <MprcSpinnerIndicator />
                                }

                                {
                                    viewData && viewData.length === 0
                                        ? <MprcFilterEmptyDataset message={t("Feed.Card.Actions.PublicationRelationshipDialog.ListEmptyMessage")} />
                                        : viewData && RenderGrid()
                                }
                            </>
                        }
                    />
                </DialogContent>
            </Dialog>
        </>
    );
};
